import { useState } from "react";
import {
  MONTH_NAMES,
  WEEKDAY_LABELS,
  addMonths,
  buildMonthGrid,
  currentYearMonth,
  todayIso,
  type YearMonth,
} from "../lib/calendar.js";

interface BlockedDatesCalendarProps {
  /** Dates already closed for booking (ISO YYYY-MM-DD). */
  blocked: readonly string[];
  /** Disables interaction while a save is in flight. */
  busy: boolean;
  /** Invoked with the inclusive range the Owner chose. */
  onSelectRange: (from: string, to: string) => void;
}

/**
 * Month calendar for closing dates, with travel-site style range selection:
 * the first click sets the start, the second sets the end, and the span between
 * them highlights. A single click followed by "Close selected" closes one day.
 *
 * A native `<input type="date">` can only hold ONE date, which is why a range
 * needs a real calendar rather than two date fields. This reuses the same pure
 * month-grid helpers and `calendar__*` styling as the public availability
 * picker, so it matches the site and adds no dependency.
 *
 * Days already closed are marked so the Owner can see current state in place,
 * and past days are non-interactive (closing them would be meaningless — they
 * are already unavailable).
 */
export function BlockedDatesCalendar({
  blocked,
  busy,
  onSelectRange,
}: BlockedDatesCalendarProps): JSX.Element {
  const [view, setView] = useState<YearMonth>(() => currentYearMonth());
  const [start, setStart] = useState<string | null>(null);
  const [end, setEnd] = useState<string | null>(null);
  // The day under the cursor, so the pending span previews while choosing.
  const [hover, setHover] = useState<string | null>(null);

  const today = todayIso();
  const cells = buildMonthGrid(view);
  const heading = `${MONTH_NAMES[view.month]} ${view.year}`;
  const blockedSet = new Set(blocked);

  // While only a start is chosen, preview the span out to the hovered day.
  const previewEnd = end ?? hover;
  const spanFrom = start && previewEnd ? (start < previewEnd ? start : previewEnd) : start;
  const spanTo = start && previewEnd ? (start < previewEnd ? previewEnd : start) : start;

  const inSpan = (iso: string): boolean =>
    spanFrom !== null && spanTo !== null && iso >= spanFrom && iso <= spanTo;

  /** First click sets the start; a second click completes the range. */
  const handleDayClick = (iso: string): void => {
    if (start === null || end !== null) {
      // Begin a fresh selection.
      setStart(iso);
      setEnd(null);
      setHover(null);
      return;
    }
    // Completing the range; normalize so a backwards pick still works.
    setEnd(iso);
    setHover(null);
  };

  const clearSelection = (): void => {
    setStart(null);
    setEnd(null);
    setHover(null);
  };

  const resolvedFrom = start && end ? (start < end ? start : end) : start;
  const resolvedTo = start && end ? (start < end ? end : start) : start;
  const dayCount =
    resolvedFrom && resolvedTo
      ? Math.round(
          (Date.parse(`${resolvedTo}T00:00:00Z`) -
            Date.parse(`${resolvedFrom}T00:00:00Z`)) /
            86_400_000,
        ) + 1
      : 0;

  return (
    <div
      className="calendar blocked-cal"
      role="group"
      aria-label="Choose dates to close"
    >
      <div className="calendar__header">
        <button
          type="button"
          className="calendar__nav"
          aria-label="Previous month"
          onClick={() => setView((v) => addMonths(v, -1))}
        >
          &#8249;
        </button>
        <h3 className="calendar__title" aria-live="polite">
          {heading}
        </h3>
        <button
          type="button"
          className="calendar__nav"
          aria-label="Next month"
          onClick={() => setView((v) => addMonths(v, 1))}
        >
          &#8250;
        </button>
      </div>

      <div className="calendar__weekdays" aria-hidden="true">
        {WEEKDAY_LABELS.map((label) => (
          <span key={label} className="calendar__weekday">
            {label}
          </span>
        ))}
      </div>

      <div className="calendar__grid">
        {cells.map((cell) => {
          const isPast = cell.iso < today;
          const selectable = cell.inMonth && !isPast && !busy;
          const isBlocked = blockedSet.has(cell.iso);
          const classes = [
            "calendar__day",
            cell.inMonth ? "" : "calendar__day--muted",
            isPast && cell.inMonth ? "calendar__day--unavailable" : "",
            isBlocked ? "blocked-cal__day--blocked" : "",
            inSpan(cell.iso) ? "blocked-cal__day--selected" : "",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <button
              key={cell.iso}
              type="button"
              className={classes}
              disabled={!selectable}
              aria-pressed={inSpan(cell.iso)}
              aria-label={`${cell.iso}${isBlocked ? " (already closed)" : ""}`}
              onMouseEnter={() =>
                start !== null && end === null ? setHover(cell.iso) : undefined
              }
              onClick={() => handleDayClick(cell.iso)}
            >
              {cell.day}
            </button>
          );
        })}
      </div>

      <div className="blocked-cal__footer">
        {resolvedFrom === null ? (
          <p className="admin-list__sub">
            Tap a day to start. Tap a second day to close a whole span.
          </p>
        ) : (
          <p className="admin-list__sub">
            Selected: <strong>{resolvedFrom}</strong>
            {resolvedTo !== resolvedFrom ? (
              <>
                {" → "}
                <strong>{resolvedTo}</strong> ({dayCount} days)
              </>
            ) : end === null ? (
              " — tap another day for a range, or close just this one."
            ) : null}
          </p>
        )}
        {/* Actions appear only once something is selected: a prominent primary
            button sitting permanently disabled reads as broken. */}
        {resolvedFrom !== null ? (
          <div className="admin-list__actions">
            <button
              type="button"
              className="admin-button admin-button--primary"
              disabled={busy}
              onClick={() => {
                if (resolvedFrom && resolvedTo) {
                  onSelectRange(resolvedFrom, resolvedTo);
                  clearSelection();
                }
              }}
            >
              {busy
                ? "Saving…"
                : resolvedTo && resolvedTo !== resolvedFrom
                  ? "Close selected dates"
                  : "Close selected date"}
            </button>
            <button
              type="button"
              className="admin-button"
              disabled={busy}
              onClick={clearSelection}
            >
              Clear
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
